import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

export interface MobileHeaderConfig {
  title?: ReactNode;
  meta?: ReactNode;
  search?: ReactNode;
}

interface MobileHeaderContextValue {
  config: MobileHeaderConfig;
  setConfig: (id: string, config: MobileHeaderConfig) => void;
  clearConfig: (id: string) => void;
  scrolled: boolean;
}

const MobileHeaderContext = createContext<MobileHeaderContextValue | undefined>(undefined);

export function MobileHeaderProvider({ children, scrolled = false }: { children: ReactNode; scrolled?: boolean }) {
  const [entries, setEntries] = useState<Record<string, MobileHeaderConfig>>({});

  const setConfig = useCallback((id: string, config: MobileHeaderConfig) => {
    setEntries((prev) => ({ ...prev, [id]: config }));
  }, []);

  const clearConfig = useCallback((id: string) => {
    setEntries((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const value = useMemo<MobileHeaderContextValue>(() => ({
    config: Object.values(entries).at(-1) ?? {},
    setConfig,
    clearConfig,
    scrolled,
  }), [entries, setConfig, clearConfig, scrolled]);

  return (
    <MobileHeaderContext.Provider value={value}>
      {children}
    </MobileHeaderContext.Provider>
  );
}

export function useMobileHeaderContext() {
  const ctx = useContext(MobileHeaderContext);
  if (!ctx) throw new Error("useMobileHeaderContext must be used within MobileHeaderProvider");
  return ctx;
}

/**
 * Animates a block's height/opacity to zero when `collapsed` is true, using the
 * grid-template-rows 0fr/1fr trick so no height measurement is needed.
 * `spacing` is the margin-bottom to animate alongside it (e.g. the gap the parent
 * flex container would otherwise leave behind once the block has zero height).
 */
export function MobileHeaderCollapse({ collapsed, children, spacing }: { collapsed: boolean; children: ReactNode; spacing?: string }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: collapsed ? "0fr" : "1fr",
        opacity: collapsed ? 0 : 1,
        transition: "grid-template-rows 220ms ease, opacity 180ms ease, margin-bottom 220ms ease",
      }}
    >
      <div style={{ minHeight: 0, overflow: "hidden" }}>{children}</div>
    </div>
  );
}

/**
 * Registers the mobile header content (title/meta/search) for the page that calls it.
 * Content is cleared automatically when the page unmounts.
 */
export function useMobileHeader(config: MobileHeaderConfig, deps: unknown[]) {
  const { setConfig, clearConfig } = useMobileHeaderContext();
  const idRef = useRef(Math.random().toString(36).slice(2));

  useEffect(() => {
    setConfig(idRef.current, config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    const id = idRef.current;
    return () => clearConfig(id);
  }, [clearConfig]);
}
